import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { LiveClock } from '@/components/os/LiveClock';
import { useAuth } from '@/features/auth/useAuth';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { StudentSummaryCard } from './shared';

/**
 * A parent's home: one card per linked child.
 *
 * <p>The list comes from GET /portal/children, resolved from the signed-in
 * guardian's own links. Each card's links carry `?child=`, which only picks
 * among these children — the server re-checks the link on every request.
 */
export function ChildrenHomePage() {
  const { user } = useAuth();

  const children = useQuery({
    queryKey: queryKeys.portal.children,
    queryFn: portalApi.children,
  });

  return (
    <div>
      <PageHeader
        title="My children"
        description={
          user?.firstName !== undefined
            ? `Welcome, ${user.firstName}. Attendance, fees and everything else for each of your children.`
            : 'Attendance, fees and everything else for each of your children.'
        }
        icon={<Users className="h-5 w-5" aria-hidden="true" />}
        action={<LiveClock timeZone={user?.schoolTimezone} />}
      />

      {children.isPending ? (
        <Card>
          <LoadingState label="Loading your children" />
        </Card>
      ) : children.isError ? (
        <Card>
          <ErrorState error={children.error} onRetry={() => void children.refetch()} />
        </Card>
      ) : children.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users className="h-6 w-6" aria-hidden="true" />}
            title="No children linked"
            description="Your account is not linked to any student yet. The school office can link your children to your account."
          />
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {children.data.map((child) => (
            <StudentSummaryCard
              key={child.studentId}
              student={child}
              subject={{ studentId: child.studentId }}
              linkFor={(path) => `${path}?child=${child.studentId}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
